import DayjsUtils from '@date-io/dayjs';
import { DatePicker as MuiDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import dayjs from 'dayjs';
import React, { useRef } from 'react';
import { TextField, InputAdornment } from '@material-ui/core';
import { ReactComponent as CalendarIcon } from './date.svg';

/**
 * A distinct id per mounted field, for the callers that pass none.
 *
 * Material wires the caption's `htmlFor` from the `id` this component is given,
 * so a field without one renders a `<label>` pointing at nothing: the control
 * has no accessible name and assistive technology announces an unlabelled text
 * box. Almost no caller passes an id, so almost every date field in the product
 * is in that state, including both fields of the shared date-range popover.
 *
 * A counter rather than a value derived from the label, because two fields on
 * one screen can legitimately carry the same caption, and two elements sharing
 * an id associate the caption with whichever comes first.
 */
let fieldSeq = 0;

const DatePicker = ({
	value,
	setValue,
	errTxts,
	disabled = false,
	disableFuture = false,
	disablePast = false,
	label,
	minDate = undefined,
	maxDate = undefined,
	width = undefined,
	placeholder,
	id,
	paddingLeft,
	paddingRight,
	dateStyle,
	required = false,
	clearable = true,
	variant,
}) => {
	const ref = useRef(null);

	// Held in a ref so the field keeps one id for its whole life; an id the
	// caller supplies still wins.
	const fallbackId = useRef(null);
	if (fallbackId.current === null) {
		fieldSeq += 1;
		fallbackId.current = `date-field-${fieldSeq}`;
	}
	const fieldId = id || fallbackId.current;

	return (
		<>
			<div
				onClick={() => ref.current.click()}
				onKeyDown={(e) => {
					if (e.key !== 'Enter') return;

					// OPENING THE PICKER IS NOT CHOOSING A DAY, and without the
					// line below one Enter does both.
					//
					// While the picker is open Material's own wrapper keeps a
					// keydown listener on `window` that maps Enter to "accept the
					// highlighted date". It registers that listener in a layout
					// effect, which React runs while this very keydown is still
					// travelling: the click below opens the picker, the listener
					// goes on, and then the same event finishes bubbling and is
					// read as the acceptance. On a field holding no value yet the
					// highlighted date is today, so the keystroke meant to open
					// the calendar silently commits today's date instead, and any
					// screen that acts on the value acts on that.
					//
					// A React-level stopPropagation would not do it: the listener
					// is a native one, above React's own, so the native event is
					// what has to be stopped. Everything React dispatches for this
					// key still runs.
					e.nativeEvent.stopPropagation();
					ref.current.click();
				}}
				role="button"
				style={{ width }}
				tabIndex={0}
			>
				<TextField
					InputLabelProps={{ shrink: true }}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<CalendarIcon />
							</InputAdornment>
						),
					}}
					className={dateStyle || ''}
					disabled={disabled}
					error={!!errTxts?.length}
					helperText={errTxts}
					id={fieldId}
					label={label}
					name="dob"
					onChange={(e) => setValue(dayjs(e.target.value).isValid() ? dayjs(e.target.value) : '')}
					onClick={(e) => e.preventDefault()}
					required={required}
					style={{ width: '100%', paddingLeft: paddingLeft || '', paddingRight: paddingRight || '' }}
					type="text"
					value={value ? dayjs(value).format('MMM DD, YYYY') : placeholder || 'mm / dd / yyyy'}
					variant={variant}
				/>
			</div>
			<MuiPickersUtilsProvider utils={DayjsUtils}>
				<MuiDatePicker
					autoOk
					clearable={clearable}
					disableFuture={disableFuture}
					disablePast={disablePast}
					disabled={disabled}
					format="dd/MM/yyyy"
					inputRef={ref}
					maxDate={maxDate}
					minDate={minDate}
					onChange={setValue}
					orientation="portrait"
					style={{ display: 'none' }}
					value={value}
				/>
			</MuiPickersUtilsProvider>
		</>
	);
};

export default DatePicker;
